import {
  applySerializedTrialForm,
  debounce,
  loadTrialForm,
  saveTrialForm,
  serializeTrialForm,
  TAX_TRIAL_SESSION_KEY
} from './lib/trialFormSession';
import { initTaxNumberInputs, parseYenInput } from './taxNumberInput';
import { ageAtYearEnd } from './lib/ageAtYearEnd';
import { yen, setYenWithZeroClass } from './lib/taxDomHelpers';
import { parseYenIntLoose } from './lib/formParse';
import { readDobFromBasicInfo, readYearFromBasicInfo } from './lib/basicInfoReader';
import {
  住宅借入金特別控除使用可否判断関数,
  住宅借入金特別控除額計算関数,
  納付税額計算関数,
  住民税納付税額計算関数,
  type 住宅情報,
  type 全収入情報,
  type 住民税計算入力,
} from '@api-lib/taxCalc';

// 非負整数を読む。use=false なら 0 を返す。エラーなら文字列を返す。
function readNonNegInt(
  form: HTMLFormElement,
  id: string,
  err: string,
  use: boolean
): number | string {
  if (!use) return 0;
  const v = parseYenInput(form.querySelector<HTMLInputElement>(id)?.value ?? '');
  if (v === null || v < 0) return err;
  return v;
}

function readHousing(form: HTMLFormElement): 住宅情報 {
  const prepay = form.querySelector<HTMLInputElement>('#tax-prepay-shorten')?.checked ?? false;
  const shortened = parseYenIntLoose(form.querySelector<HTMLInputElement>('#tax-shorten-years')?.value ?? '', 0);
  const kubunRaw = form.querySelector<HTMLSelectElement>('#tax-housing-kubun')?.value;
  const 住宅区分: 住宅情報['住宅区分'] =
    kubunRaw === '認定' || kubunRaw === 'ZEH' || kubunRaw === '省エネ' || kubunRaw === '一般'
      ? kubunRaw
      : '一般';

  return {
    年末ローン残高: Math.max(0, parseYenIntLoose(form.querySelector<HTMLInputElement>('#tax-loan-balance')?.value ?? '', 0)),
    住宅区分,
    居住年数: Math.max(0, parseYenIntLoose(form.querySelector<HTMLInputElement>('#tax-live-years')?.value ?? '', 0)),
    住宅借入金を使ったか: form.querySelector<HTMLInputElement>('#tax-used-loan')?.checked ?? false,
    床面積50平米以上かつ半分以上居住用か: form.querySelector<HTMLInputElement>('#tax-floor50')?.checked ?? false,
    西暦何年にローンを組んだか: parseYenIntLoose(
      form.querySelector<HTMLInputElement>('#tax-loan-start-year')?.value ?? '',
      2020
    ),
    何年分のローンを組んだか: Math.max(0, parseYenIntLoose(form.querySelector<HTMLInputElement>('#tax-loan-term-years')?.value ?? '', 0)),
    期間短縮型の繰り上げ決済を行ったか: prepay,
    期間短縮型の繰り上げ決済を行った場合何年分の期間を短縮したか: prepay ? Math.max(0, shortened) : 0
  };
}

/** 入力された収入の粗い年間合計（税前キャッシュフローに近い値） */
function estimateAnnualCashApprox(form: HTMLFormElement, n: FormNumbers): number {
  const hasTemp = form.querySelector<HTMLInputElement>('#tax-opt-temp')?.checked ?? false;
  const hasMisc = form.querySelector<HTMLInputElement>('#tax-opt-misc')?.checked ?? false;
  const hasSanrin = form.querySelector<HTMLInputElement>('#tax-opt-sanrin')?.checked ?? false;
  const hasRental = form.querySelector<HTMLInputElement>('#tax-opt-rental')?.checked ?? false;
  const hasBusiness = form.querySelector<HTMLInputElement>('#tax-opt-business')?.checked ?? false;
  const hasTfx = form.querySelector<HTMLInputElement>('#tax-opt-transfer-other')?.checked ?? false;
  const hasTre = form.querySelector<HTMLInputElement>('#tax-opt-transfer-re')?.checked ?? false;

  let total = n.salary;
  if (hasTemp) total += Math.max(0, n.一時収入 - n.一時収入にかかった経費);
  if (hasMisc) {
    total += n.公的老齢年金収入;
    total += Math.max(0, n.その他雑収入 - n.その他雑収入にかかった経費);
  }
  if (hasSanrin) total += Math.max(0, n.山林収入 - n.山林必要経費);
  if (hasRental) total += Math.max(0, n.家賃収入 + n.礼金更新料 - n.不動産必要経費);
  if (hasBusiness) total += Math.max(0, n.事業総収入 - n.事業必要経費);
  if (hasTfx) total += Math.max(0, n.tfx譲渡価額 - n.tfx取得費 - n.tfx譲渡費用);
  if (hasTre) total += Math.max(0, n.tre譲渡価額 - n.tre取得費 - n.tre譲渡費用);
  return total;
}

type 青色申告情報型 = NonNullable<全収入情報['青色申告情報']>;

function read青色申告情報(form: HTMLFormElement): 青色申告情報型 {
  return {
    青色申告承認申請書を提出しているか:
      form.querySelector<HTMLInputElement>('#tax-blue-approved')?.checked ?? false,
    事業所得または事業的規模の不動産所得があるか:
      form.querySelector<HTMLInputElement>('#tax-blue-business-scale')?.checked ?? false,
    複式簿記で記帳しているか: form.querySelector<HTMLInputElement>('#tax-blue-bookkeeping')?.checked ?? false,
    貸借対照表と損益計算書を添付しているか:
      form.querySelector<HTMLInputElement>('#tax-blue-bs-pl')?.checked ?? false,
    期限内に申告しているか: form.querySelector<HTMLInputElement>('#tax-blue-deadline')?.checked ?? false,
    eTaxで電子申告または電子帳簿保存をしているか:
      form.querySelector<HTMLInputElement>('#tax-blue-etax')?.checked ?? false
  };
}

type FormNumbers = {
  age: number;
  year: number;
  salary: number;
  一時収入: number;
  一時収入にかかった経費: number;
  公的老齢年金収入: number;
  その他雑収入: number;
  その他雑収入にかかった経費: number;
  山林収入: number;
  山林必要経費: number;
  家賃収入: number;
  礼金更新料: number;
  不動産必要経費: number;
  事業総収入: number;
  事業必要経費: number;
  tfx譲渡価額: number;
  tfx取得費: number;
  tfx譲渡費用: number;
  tre譲渡価額: number;
  tre取得費: number;
  tre譲渡費用: number;
};

function readFormNumbers(form: HTMLFormElement): FormNumbers | string {
  const hasTemp = form.querySelector<HTMLInputElement>('#tax-opt-temp')?.checked ?? false;
  const hasMisc = form.querySelector<HTMLInputElement>('#tax-opt-misc')?.checked ?? false;
  const hasSanrin = form.querySelector<HTMLInputElement>('#tax-opt-sanrin')?.checked ?? false;
  const hasRental = form.querySelector<HTMLInputElement>('#tax-opt-rental')?.checked ?? false;
  const hasBusiness = form.querySelector<HTMLInputElement>('#tax-opt-business')?.checked ?? false;
  const hasTfx = form.querySelector<HTMLInputElement>('#tax-opt-transfer-other')?.checked ?? false;
  const hasTre = form.querySelector<HTMLInputElement>('#tax-opt-transfer-re')?.checked ?? false;

  const yearResult = readYearFromBasicInfo();
  if (typeof yearResult === 'string') return yearResult;

  const dob = readDobFromBasicInfo();
  if (!dob.ok) return dob.message;

  const salary = parseYenInput(form.querySelector<HTMLInputElement>('#tax-salary')?.value ?? '');
  if (salary === null || salary < 0) return '給与収入は 0 以上の整数で入力してね。';

  const age = ageAtYearEnd(yearResult, dob.y, dob.m, dob.d);
  if (age < 0) return '生年月日が対象年の年末より後になっています。';
  if (age > 120) return '年齢の試算が 120 歳を超えています。生年月日を確認してね。';

  const tempIn = readNonNegInt(form, '#tax-temp-income', '一時収入は 0 以上の整数で入力してね。', hasTemp);
  if (typeof tempIn === 'string') return tempIn;
  const tempEx = readNonNegInt(form, '#tax-temp-expense', '一時収入の経費は 0 以上の整数で入力してね。', hasTemp);
  if (typeof tempEx === 'string') return tempEx;

  const pension = readNonNegInt(form, '#tax-pension', '公的年金等収入は 0 以上の整数で入力してね。', hasMisc);
  if (typeof pension === 'string') return pension;
  const miscIn = readNonNegInt(form, '#tax-misc-income', 'その他雑収入は 0 以上の整数で入力してね。', hasMisc);
  if (typeof miscIn === 'string') return miscIn;
  const miscEx = readNonNegInt(form, '#tax-misc-expense', 'その他雑収入の経費は 0 以上の整数で入力してね。', hasMisc);
  if (typeof miscEx === 'string') return miscEx;

  const sanRev = readNonNegInt(form, '#tax-sanrin-revenue', '山林収入は 0 以上の整数で入力してね。', hasSanrin);
  if (typeof sanRev === 'string') return sanRev;
  const sanExp = readNonNegInt(form, '#tax-sanrin-expense', '山林必要経費は 0 以上の整数で入力してね。', hasSanrin);
  if (typeof sanExp === 'string') return sanExp;

  const rentRev = readNonNegInt(form, '#tax-rent-revenue', '家賃収入は 0 以上の整数で入力してね。', hasRental);
  if (typeof rentRev === 'string') return rentRev;
  const rentKey = readNonNegInt(form, '#tax-rent-keymoney', '礼金・更新料は 0 以上の整数で入力してね。', hasRental);
  if (typeof rentKey === 'string') return rentKey;
  const rentExp = readNonNegInt(form, '#tax-rent-expense', '不動産の必要経費は 0 以上の整数で入力してね。', hasRental);
  if (typeof rentExp === 'string') return rentExp;

  const bizRev = readNonNegInt(form, '#tax-biz-revenue', '事業の総収入は 0 以上の整数で入力してね。', hasBusiness);
  if (typeof bizRev === 'string') return bizRev;
  const bizExp = readNonNegInt(form, '#tax-biz-expense', '事業の必要経費は 0 以上の整数で入力してね。', hasBusiness);
  if (typeof bizExp === 'string') return bizExp;

  const tfxP = readNonNegInt(form, '#tax-tfx-price', '譲渡価額は 0 以上の整数で入力してね。', hasTfx);
  if (typeof tfxP === 'string') return tfxP;
  const tfxC = readNonNegInt(form, '#tax-tfx-cost', '取得費は 0 以上の整数で入力してね。', hasTfx);
  if (typeof tfxC === 'string') return tfxC;
  const tfxF = readNonNegInt(form, '#tax-tfx-fee', '譲渡費用は 0 以上の整数で入力してね。', hasTfx);
  if (typeof tfxF === 'string') return tfxF;

  const treP = readNonNegInt(form, '#tax-tre-price', '不動産譲渡の譲渡価額は 0 以上の整数で入力してね。', hasTre);
  if (typeof treP === 'string') return treP;
  const treC = readNonNegInt(form, '#tax-tre-cost', '不動産譲渡の取得費は 0 以上の整数で入力してね。', hasTre);
  if (typeof treC === 'string') return treC;
  const treF = readNonNegInt(form, '#tax-tre-fee', '不動産譲渡の譲渡費用は 0 以上の整数で入力してね。', hasTre);
  if (typeof treF === 'string') return treF;

  return {
    age,
    year: yearResult,
    salary,
    一時収入: tempIn,
    一時収入にかかった経費: tempEx,
    公的老齢年金収入: pension,
    その他雑収入: miscIn,
    その他雑収入にかかった経費: miscEx,
    山林収入: sanRev,
    山林必要経費: sanExp,
    家賃収入: rentRev,
    礼金更新料: rentKey,
    不動産必要経費: rentExp,
    事業総収入: bizRev,
    事業必要経費: bizExp,
    tfx譲渡価額: tfxP,
    tfx取得費: tfxC,
    tfx譲渡費用: tfxF,
    tre譲渡価額: treP,
    tre取得費: treC,
    tre譲渡費用: treF
  };
}

function build全収入情報(form: HTMLFormElement, n: FormNumbers): 全収入情報 {
  const mortgageOpt = form.querySelector<HTMLInputElement>('#tax-opt-mortgage')?.checked ?? false;
  const hasTemp = form.querySelector<HTMLInputElement>('#tax-opt-temp')?.checked ?? false;
  const hasMisc = form.querySelector<HTMLInputElement>('#tax-opt-misc')?.checked ?? false;
  const hasSanrin = form.querySelector<HTMLInputElement>('#tax-opt-sanrin')?.checked ?? false;
  const hasRental = form.querySelector<HTMLInputElement>('#tax-opt-rental')?.checked ?? false;
  const hasBusiness = form.querySelector<HTMLInputElement>('#tax-opt-business')?.checked ?? false;
  const hasTfx = form.querySelector<HTMLInputElement>('#tax-opt-transfer-other')?.checked ?? false;
  const hasTre = form.querySelector<HTMLInputElement>('#tax-opt-transfer-re')?.checked ?? false;
  const hasBlue = form.querySelector<HTMLInputElement>('#tax-opt-blue')?.checked ?? false;

  return {
    年齢: n.age,
    対象年: n.year,
    学生かどうか: form.querySelector<HTMLInputElement>('#tax-student')?.checked ?? false,
    給与収入: n.salary,
    一時所得計算のための情報: hasTemp
      ? { 一時収入: n.一時収入, 一時収入にかかった経費: n.一時収入にかかった経費 }
      : null,
    雑所得計算のための情報: hasMisc
      ? {
          公的老齢年金収入: n.公的老齢年金収入,
          その他雑収入: n.その他雑収入,
          その他雑収入にかかった経費: n.その他雑収入にかかった経費
        }
      : null,
    山林所得計算のための情報: hasSanrin
      ? { 山林収入: n.山林収入, 山林必要経費: n.山林必要経費 }
      : null,
    不動産所得計算のための情報: hasRental
      ? {
          家賃収入: n.家賃収入,
          礼金更新料: n.礼金更新料,
          必要経費: n.不動産必要経費
        }
      : null,
    不動産の譲渡所得計算のための情報: hasTre
      ? {
          譲渡価額: n.tre譲渡価額,
          取得費: n.tre取得費,
          譲渡費用: n.tre譲渡費用,
          所有期間は5年超か: form.querySelector<HTMLInputElement>('#tax-tre-long')?.checked ?? false
        }
      : null,
    不動産以外の譲渡所得計算のための情報: hasTfx
      ? {
          譲渡価額: n.tfx譲渡価額,
          取得費: n.tfx取得費,
          譲渡費用: n.tfx譲渡費用,
          所有期間は5年超か: form.querySelector<HTMLInputElement>('#tax-tfx-long')?.checked ?? false
        }
      : null,
    事業所得計算のための情報: hasBusiness
      ? {
          総収入金額: n.事業総収入,
          必要経費: n.事業必要経費
        }
      : null,
    青色申告情報: hasBlue ? read青色申告情報(form) : null,
    住宅情報: mortgageOpt ? readHousing(form) : null
  };
}

function syncIncomeOptionSections(form: HTMLFormElement): void {
  const pairs: [string, string, string][] = [
    ['#tax-opt-temp', '#tax-section-temp', '#tax-fieldset-temp'],
    ['#tax-opt-misc', '#tax-section-misc', '#tax-fieldset-misc'],
    ['#tax-opt-sanrin', '#tax-section-sanrin', '#tax-fieldset-sanrin'],
    ['#tax-opt-rental', '#tax-section-rental', '#tax-fieldset-rental'],
    ['#tax-opt-business', '#tax-section-business', '#tax-fieldset-business'],
    ['#tax-opt-transfer-other', '#tax-section-transfer-other', '#tax-fieldset-transfer-other'],
    ['#tax-opt-transfer-re', '#tax-section-transfer-re', '#tax-fieldset-transfer-re'],
    ['#tax-opt-blue', '#tax-section-blue', '#tax-fieldset-blue'],
    ['#tax-opt-mortgage', '#tax-section-housing', '#tax-housing-fieldset'],
  ];
  for (const [optSel, secSel, fsSel] of pairs) {
    const on = form.querySelector<HTMLInputElement>(optSel)?.checked ?? false;
    const sec = form.querySelector<HTMLElement>(secSel);
    const fs = form.querySelector<HTMLFieldSetElement>(fsSel);
    if (sec) sec.hidden = !on;
    if (fs) fs.disabled = !on;
  }
}

export function initTaxShell(root: HTMLElement): void {
  initTaxNumberInputs(root);
  const basicInfo = document.getElementById('basic-info-menu');
  if (basicInfo) initTaxNumberInputs(basicInfo);

  // --- コアUI要素 ---
  const form = root.querySelector<HTMLFormElement>('#tax-form');
  const errEl = root.querySelector<HTMLElement>('#tax-error');
  const placeholderEl = root.querySelector<HTMLElement>('#tax-result-placeholder');
  const resultContent = root.querySelector<HTMLElement>('#tax-result-content');
  // サマリー表示欄
  const outShotokuZei = root.querySelector<HTMLElement>('#tax-out-shotoku-zei');
  const outJuminZei = root.querySelector<HTMLElement>('#tax-out-jumin-zei');
  const outTotal = root.querySelector<HTMLElement>('#tax-out-total');
  const outTemoto = root.querySelector<HTMLElement>('#tax-out-temoto');
  // モーダル・トリガー
  const modalShotoku = root.querySelector<HTMLDialogElement>('#modal-shotoku');
  const modalJumin = root.querySelector<HTMLDialogElement>('#modal-jumin');
  const btnDetailShotoku = root.querySelector<HTMLButtonElement>('#tax-btn-detail-shotoku');
  const btnDetailJumin = root.querySelector<HTMLButtonElement>('#tax-btn-detail-jumin');
  const modalCloseShotoku = root.querySelector<HTMLButtonElement>('#tax-modal-close-shotoku');
  const modalCloseJumin = root.querySelector<HTMLButtonElement>('#tax-modal-close-jumin');

  if (
    !form || !errEl || !placeholderEl || !resultContent ||
    !outShotokuZei || !outJuminZei || !outTotal || !outTemoto ||
    !modalShotoku || !modalJumin ||
    !btnDetailShotoku || !btnDetailJumin ||
    !modalCloseShotoku || !modalCloseJumin
  ) {
    return;
  }

  // --- 所得税モーダル内の詳細要素 ---
  const outKyuyoShotoku = modalShotoku.querySelector<HTMLElement>('#tax-out-kyuyo-shotoku')!;
  const outIchiji = modalShotoku.querySelector<HTMLElement>('#tax-out-ichiji')!;
  const outZatsu = modalShotoku.querySelector<HTMLElement>('#tax-out-zatsu')!;
  const outFudosan = modalShotoku.querySelector<HTMLElement>('#tax-out-fudosan')!;
  const outJigyo = modalShotoku.querySelector<HTMLElement>('#tax-out-jigyo')!;
  const outTfx = modalShotoku.querySelector<HTMLElement>('#tax-out-tfx')!;
  const outSoshotoku = modalShotoku.querySelector<HTMLElement>('#tax-out-soshotoku')!;
  const outKyuyoKazei = modalShotoku.querySelector<HTMLElement>('#tax-out-kyuyo-kazei')!;
  const outKyuyoZei = modalShotoku.querySelector<HTMLElement>('#tax-out-kyuyo-zei')!;
  const outSanrinShotoku = modalShotoku.querySelector<HTMLElement>('#tax-out-sanrin-shotoku')!;
  const outSanrinKazei = modalShotoku.querySelector<HTMLElement>('#tax-out-sanrin-kazei')!;
  const outSanrinZei = modalShotoku.querySelector<HTMLElement>('#tax-out-sanrin-zei')!;
  const outTreShotoku = modalShotoku.querySelector<HTMLElement>('#tax-out-tre-shotoku')!;
  const outTreZei = modalShotoku.querySelector<HTMLElement>('#tax-out-tre-zei')!;
  const outLoanOk = modalShotoku.querySelector<HTMLElement>('#tax-out-loan-ok')!;
  const outLoanDed = modalShotoku.querySelector<HTMLElement>('#tax-out-loan-ded')!;
  const outNoufu = modalShotoku.querySelector<HTMLElement>('#tax-out-noufu')!;

  // --- 住民税モーダル内の詳細要素 ---
  const jtOutKyuyo = modalJumin.querySelector<HTMLElement>('#jt-out-kyuyo')!;
  const jtOutIchiji = modalJumin.querySelector<HTMLElement>('#jt-out-ichiji')!;
  const jtOutZatsu = modalJumin.querySelector<HTMLElement>('#jt-out-zatsu')!;
  const jtOutFudosan = modalJumin.querySelector<HTMLElement>('#jt-out-fudosan')!;
  const jtOutJigyo = modalJumin.querySelector<HTMLElement>('#jt-out-jigyo')!;
  const jtOutTfx = modalJumin.querySelector<HTMLElement>('#jt-out-tfx')!;
  const jtOutSoshotoku = modalJumin.querySelector<HTMLElement>('#jt-out-soshotoku')!;
  const jtOutKojo = modalJumin.querySelector<HTMLElement>('#jt-out-kojo')!;
  const jtOutKazei = modalJumin.querySelector<HTMLElement>('#jt-out-kazei')!;
  const jtOutShotokuwari = modalJumin.querySelector<HTMLElement>('#jt-out-shotokuwari')!;
  const jtOutSanrin = modalJumin.querySelector<HTMLElement>('#jt-out-sanrin')!;
  const jtOutSanrinZei = modalJumin.querySelector<HTMLElement>('#jt-out-sanrin-zei')!;
  const jtOutTre = modalJumin.querySelector<HTMLElement>('#jt-out-tre')!;
  const jtOutTreRate = modalJumin.querySelector<HTMLElement>('#jt-out-tre-rate')!;
  const jtOutTreZei = modalJumin.querySelector<HTMLElement>('#jt-out-tre-zei')!;
  const jtOutWariTotal = modalJumin.querySelector<HTMLElement>('#jt-out-wari-total')!;
  const jtOutChosei = modalJumin.querySelector<HTMLElement>('#jt-out-chosei')!;
  const jtOutLoan = modalJumin.querySelector<HTMLElement>('#jt-out-loan')!;
  const jtOutKintowari = modalJumin.querySelector<HTMLElement>('#jt-out-kintowari')!;
  const jtOutNoufu = modalJumin.querySelector<HTMLElement>('#jt-out-noufu')!;

  const persistTaxDraft = (): void => {
    const prev = loadTrialForm(TAX_TRIAL_SESSION_KEY);
    saveTrialForm(TAX_TRIAL_SESSION_KEY, {
      v: 1,
      fields: serializeTrialForm(form),
      hasCalculated: prev?.hasCalculated ?? false
    });
  };
  const debouncedPersistTax = debounce(persistTaxDraft, 400);

  const taxPersisted = loadTrialForm(TAX_TRIAL_SESSION_KEY);
  if (taxPersisted) {
    applySerializedTrialForm(taxPersisted.fields);
  }

  // --- フォーム変更でオプションセクション切替 ---
  const onFormChange = (): void => {
    syncIncomeOptionSections(form);
    debouncedPersistTax();
  };
  form.addEventListener('change', onFormChange);
  form.addEventListener('input', debouncedPersistTax);
  syncIncomeOptionSections(form);

  // --- モーダル開閉 ---
  btnDetailShotoku.addEventListener('click', () => { modalShotoku.showModal(); });
  btnDetailJumin.addEventListener('click', () => { modalJumin.showModal(); });
  modalCloseShotoku.addEventListener('click', () => { modalShotoku.close(); });
  modalCloseJumin.addEventListener('click', () => { modalJumin.close(); });
  // バックドロップクリックで閉じる
  for (const modal of [modalShotoku, modalJumin]) {
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
  }
  

  const basicHelpDialog = root.querySelector<HTMLDialogElement>('#tax-basic-help-dialog');
  const basicHelpOpen = root.querySelector<HTMLButtonElement>('#tax-basic-help-open');
  const basicHelpClose = root.querySelector<HTMLButtonElement>('#tax-basic-help-close');
  if (basicHelpDialog && basicHelpOpen && basicHelpClose) {
    basicHelpOpen.addEventListener('click', () => {
      basicHelpDialog.showModal();
    });
    basicHelpClose.addEventListener('click', () => {
      basicHelpDialog.close();
    });
    basicHelpDialog.addEventListener('click', (e) => {
      if (e.target === basicHelpDialog) basicHelpDialog.close();
    });
  }

  const studentHelpDialog = root.querySelector<HTMLDialogElement>('#tax-student-help-dialog');
  const studentHelpOpen = root.querySelector<HTMLButtonElement>('#tax-student-help-open');
  const studentHelpClose = root.querySelector<HTMLButtonElement>('#tax-student-help-close');
  if (studentHelpDialog && studentHelpOpen && studentHelpClose) {
    studentHelpOpen.addEventListener('click', () => {
      studentHelpDialog.showModal();
    });
    studentHelpClose.addEventListener('click', () => {
      studentHelpDialog.close();
    });
    studentHelpDialog.addEventListener('click', (e) => {
      if (e.target === studentHelpDialog) studentHelpDialog.close();
    });
  }

  const wireHelpTip = (dialogId: string, openId: string, closeId: string): void => {
    const dialog = root.querySelector<HTMLDialogElement>(dialogId);
    const openBtn = root.querySelector<HTMLButtonElement>(openId);
    const closeBtn = root.querySelector<HTMLButtonElement>(closeId);
    if (!dialog || !openBtn || !closeBtn) return;
    openBtn.addEventListener('click', () => {
      dialog.showModal();
    });
    closeBtn.addEventListener('click', () => {
      dialog.close();
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
  };

  wireHelpTip('#tax-temp-help-dialog', '#tax-temp-help-open', '#tax-temp-help-close');
  wireHelpTip('#tax-misc-help-dialog', '#tax-misc-help-open', '#tax-misc-help-close');
  wireHelpTip('#tax-sanrin-help-dialog', '#tax-sanrin-help-open', '#tax-sanrin-help-close');
  wireHelpTip('#tax-rental-help-dialog', '#tax-rental-help-open', '#tax-rental-help-close');
  wireHelpTip('#tax-business-help-dialog', '#tax-business-help-open', '#tax-business-help-close');
  wireHelpTip('#tax-tfx-help-dialog', '#tax-tfx-help-open', '#tax-tfx-help-close');
  wireHelpTip('#tax-tre-help-dialog', '#tax-tre-help-open', '#tax-tre-help-close');

  // --- 計算実行 ---
  const runTaxCalculation = (): boolean => {
    errEl.hidden = true;
    errEl.textContent = '';

    const nums = readFormNumbers(form);
    if (typeof nums === 'string') {
      errEl.textContent = nums;
      errEl.hidden = false;
      return false;
    }

    const input = build全収入情報(form, nums);

    // 所得税計算
    const full = 納付税額計算関数(input);
    const { 総合課税額結果, 山林所得結果, 不動産の譲渡所得結果, 納付税額 } = full;

    const 合計所得 =
      総合課税額結果.総所得 + 山林所得結果.山林所得 + 不動産の譲渡所得結果.不動産の譲渡所得;

    let loanOkText: string;
    let loanDed: number;
    if (!input.住宅情報) {
      loanOkText = '対象外（ローン入力なし）';
      loanDed = 0;
    } else {
      const ok = 住宅借入金特別控除使用可否判断関数(input, 合計所得);
      loanOkText = ok ? '利用可（試算）' : '利用不可（試算）';
      loanDed = ok ? 住宅借入金特別控除額計算関数(input) : 0;
    }

    // 住民税用: 所得税で引ききれなかった住宅ローン控除残額
    const 所得税額合計before控除 =
      総合課税額結果.給与所得税額 +
      山林所得結果.山林所得税額 +
      不動産の譲渡所得結果.不動産の譲渡所得税額;
    const 住宅控除残額 = Math.max(0, loanDed - 所得税額合計before控除);
    const 所得税課税総所得等合計 =
      総合課税額結果.課税給与所得 +
      山林所得結果.課税山林所得 +
      不動産の譲渡所得結果.不動産の課税譲渡所得;

    // 住民税計算（配偶者・扶養・生命保険料は暫定 0 で試算）
    const juminInput: 住民税計算入力 = {
      所得計算入力: input,
      人的控除情報: {
        一般配偶者控除対象か: false,
        老人配偶者控除対象か: false,
        一般扶養親族数: 0,
        特定扶養親族数: 0,
        老人扶養親族数: 0,
        生命保険料控除対象額: 0,
      },
      所得税サマリ: {
        所得税で引ききれなかった住宅ローン控除残額: 住宅控除残額,
        所得税の課税総所得金額等合計: 所得税課税総所得等合計,
      },
    };
    const juminFull = 住民税納付税額計算関数(juminInput);
    const { 総合課税結果: jt総合, 山林所得結果: jt山林, 不動産の譲渡所得結果: jt不動産 } = juminFull;

    // === サマリー出力 ===
    setYenWithZeroClass(outShotokuZei, 納付税額);
    setYenWithZeroClass(outJuminZei, juminFull.住民税納付税額);
    setYenWithZeroClass(outTotal, 納付税額 + juminFull.住民税納付税額);
    const cashApprox = estimateAnnualCashApprox(form, nums);
    setYenWithZeroClass(outTemoto, cashApprox - 納付税額 - juminFull.住民税納付税額);

    // === 所得税 詳細モーダル ===
    setYenWithZeroClass(outKyuyoShotoku, 総合課税額結果.給与所得);
    setYenWithZeroClass(outIchiji, 総合課税額結果.一時所得);
    setYenWithZeroClass(outZatsu, 総合課税額結果.雑所得);
    setYenWithZeroClass(outFudosan, 総合課税額結果.不動産所得);
    setYenWithZeroClass(outJigyo, 総合課税額結果.事業所得);
    setYenWithZeroClass(outTfx, 総合課税額結果.不動産以外の譲渡所得);
    setYenWithZeroClass(outSoshotoku, 総合課税額結果.総所得);
    setYenWithZeroClass(outKyuyoKazei, 総合課税額結果.課税給与所得);
    setYenWithZeroClass(outKyuyoZei, 総合課税額結果.給与所得税額);
    setYenWithZeroClass(outSanrinShotoku, 山林所得結果.山林所得);
    setYenWithZeroClass(outSanrinKazei, 山林所得結果.課税山林所得);
    setYenWithZeroClass(outSanrinZei, 山林所得結果.山林所得税額);
    setYenWithZeroClass(outTreShotoku, 不動産の譲渡所得結果.不動産の譲渡所得);
    setYenWithZeroClass(outTreZei, 不動産の譲渡所得結果.不動産の譲渡所得税額);
    outLoanOk.textContent = loanOkText;
    setYenWithZeroClass(outLoanDed, loanDed);
    setYenWithZeroClass(outNoufu, 納付税額);

    // === 住民税 詳細モーダル ===
    setYenWithZeroClass(jtOutKyuyo, jt総合.給与所得);
    setYenWithZeroClass(jtOutIchiji, jt総合.一時所得);
    setYenWithZeroClass(jtOutZatsu, jt総合.雑所得);
    setYenWithZeroClass(jtOutFudosan, jt総合.不動産所得);
    setYenWithZeroClass(jtOutJigyo, jt総合.事業所得);
    setYenWithZeroClass(jtOutTfx, jt総合.不動産以外の譲渡所得);
    setYenWithZeroClass(jtOutSoshotoku, jt総合.総所得);
    setYenWithZeroClass(jtOutKojo, jt総合.所得控除合計);
    setYenWithZeroClass(jtOutKazei, jt総合.課税総所得);
    setYenWithZeroClass(jtOutShotokuwari, jt総合.総合課税所得割);
    setYenWithZeroClass(jtOutSanrin, jt山林.山林所得);
    setYenWithZeroClass(jtOutSanrinZei, jt山林.山林所得割);
    setYenWithZeroClass(jtOutTre, jt不動産.不動産の譲渡所得);
    jtOutTreRate.textContent =
      jt不動産.適用税率 > 0 ? `${(jt不動産.適用税率 * 100).toFixed(1)}%` : '--';
    jtOutTreRate.classList.remove('tax-result-value--zero');
    setYenWithZeroClass(jtOutTreZei, jt不動産.不動産の譲渡所得割);
    setYenWithZeroClass(jtOutWariTotal, juminFull.所得割合計);
    setYenWithZeroClass(jtOutChosei, juminFull.調整控除額);
    setYenWithZeroClass(jtOutLoan, juminFull.住宅ローン控除適用額);
    setYenWithZeroClass(jtOutKintowari, juminFull.均等割);
    setYenWithZeroClass(jtOutNoufu, juminFull.住民税納付税額);

    placeholderEl.hidden = true;
    resultContent.hidden = false;
    return true;
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (runTaxCalculation()) {
      saveTrialForm(TAX_TRIAL_SESSION_KEY, {
        v: 1,
        fields: serializeTrialForm(form),
        hasCalculated: true
      });
    }
  });

  if (taxPersisted?.hasCalculated) {
    runTaxCalculation();
  }
}

// yen は taxDomHelpers から再エクスポート（taxOutlet が直接使う場合に備える）
export { yen };
